'use client';

import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import "@/styles/collectionsSearchBar.css";

interface CollectionsSearchBarProps {
  /** Valor atual da busca */
  value: string;
  /** Callback quando o valor muda */
  onChange: (value: string) => void;
  /** Sugestão "Você quis dizer...?" */
  suggestion?: string | null;
  /** Callback quando o usuário clica na sugestão */
  onSuggestionClick?: (suggestion: string) => void;
  /** Número de resultados encontrados */
  resultsCount?: number;
  /** Se está buscando */
  isSearching?: boolean;
  /** Placeholder customizado */
  placeholder?: string;
  /** Classe CSS adicional */
  className?: string;
}

export default function CollectionsSearchBar({
  value,
  onChange,
  suggestion,
  onSuggestionClick,
  resultsCount,
  isSearching = false,
  placeholder = "Pesquisar coleções...",
  className
}: CollectionsSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Debounce interno para evitar muitas atualizações
  const [internalValue, setInternalValue] = useState(value);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sincroniza valor externo
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInternalValue(newValue);

    // Debounce de 300ms
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onChange(newValue);
    }, 300);
  }, [onChange]);

  const handleClear = useCallback(() => {
    setInternalValue('');
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);

  const handleSuggestionClick = useCallback(() => {
    if (suggestion && onSuggestionClick) {
      onSuggestionClick(suggestion);
    }
  }, [suggestion, onSuggestionClick]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    // Limpar com Escape
    if (event.key === 'Escape') {
      handleClear();
    }
  }, [handleClear]);

  // Cleanup do debounce
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const hasQuery = value.trim().length > 0;
  const showSuggestion = suggestion && hasQuery && !isSearching;
  const showResultsCount = hasQuery && typeof resultsCount === 'number' && !isSearching;

  return (
    <div className={clsx("collections-search-container", className)}>
      {/* Barra de busca */}
      <div
        className={clsx(
          "collections-search-bar",
          "relative flex items-center gap-3 rounded-2xl border bg-brand-surface/30 px-5 py-3 shadow-lg backdrop-blur-xl transition-all ring-0 focus-within:ring-0",
          isFocused
            ? "border-white/20 bg-brand-surface/40"
            : "border-white/10 hover:border-white/20 hover:bg-brand-surface/40"
        )}
      >
        {/* Ícone de busca */}
        <FontAwesomeIcon
          icon={faSearch}
          className="h-5 w-5 text-white/40 transition-colors"
        />

        {/* Input */}
        <input
          ref={inputRef}
          type="search"
          value={internalValue}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-base text-white placeholder-white/40 outline-none focus:ring-0 focus:outline-none focus-visible:outline-none"
          aria-label="Pesquisar coleções"
        />

        {/* Botão limpar */}
        {hasQuery && (
          <button
            type="button"
            onClick={handleClear}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/60 transition-all hover:bg-white/20 hover:text-white"
            aria-label="Limpar busca"
          >
            <FontAwesomeIcon icon={faTimes} className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Indicador de loading */}
        {isSearching && (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-brand-secondary" />
        )}

        {/* Botão de pesquisar (estilo Home) */}
        <button
          type="submit"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gradient text-white shadow-lg transition-transform hover:scale-110 active:scale-95"
          aria-label="Pesquisar"
        >
          <FontAwesomeIcon icon={faSearch} className="h-4 w-4" />
        </button>
      </div>

      {/* Área de feedback */}
      <div className="mt-3 min-h-[24px] px-2">
        {/* Sugestão "Você quis dizer...?" */}
        {showSuggestion && (
          <button
            type="button"
            onClick={handleSuggestionClick}
            className="group inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-brand-secondary"
          >
            <FontAwesomeIcon icon={faLightbulb} className="h-3.5 w-3.5 text-yellow-400/70 group-hover:text-yellow-400" />
            <span>
              Você quis dizer:{" "}
              <span className="font-semibold text-brand-secondary underline underline-offset-2">
                {suggestion}
              </span>
              ?
            </span>
          </button>
        )}

        {/* Contagem de resultados */}
        {showResultsCount && !showSuggestion && (
          <p className="text-sm text-white/50">
            {resultsCount === 0 ? (
              <>
                Nenhuma coleção encontrada para{" "}
                <span className="font-medium text-white/70">&quot;{value}&quot;</span>
              </>
            ) : (
              <>
                <span className="font-semibold text-brand-secondary">{resultsCount}</span>
                {resultsCount === 1 ? " resultado encontrado" : " resultados encontrados"} para{" "}
                <span className="font-medium text-white/70">&quot;{value}&quot;</span>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
